import { useCallback, useEffect, useState } from "react";
import {
  buildClientSafeLaunchProofView,
  type ClientSafeLaunchProofViewV1,
} from "@enrein/shared-contracts";
import { clientSafeProofReferenceLabel } from "@enrein/shared-contracts/reports/client-safe-proof-reference-label";
import badgeUrl from "@enrein/design-system/assets/enrein-badge-launch-proof.svg";
import { toClientApiErrorMessage } from "@enrein/shared-contracts/display/tier1-client-display";
import type { ClientPortalApiClient } from "./api.js";
import {
  buildLaunchProofInputFromPortalApis,
  loadPortalLaunchProofApiSnapshot,
} from "./build-launch-proof-input-from-portal-apis.js";
import { isClientEvidenceNotReadyError } from "./client-evidence-not-ready.js";

function chipClass(statusLabel: string): string {
  const lower = statusLabel.toLowerCase();
  if (lower.includes("pass") || lower.includes("ready")) return "check-chip check-chip--pass";
  if (lower.includes("warn") || lower.includes("attention")) return "check-chip check-chip--warn";
  return "check-chip";
}

export function LaunchProofSummaryPanel({
  api,
  clientLinkId,
  token,
  enabled = true,
}: {
  readonly api: ClientPortalApiClient;
  readonly clientLinkId: string;
  readonly token: string;
  /** When false, skip fetch so hidden Proof tabs cannot fail-closed before approval. */
  readonly enabled?: boolean;
}) {
  const [view, setView] = useState<ClientSafeLaunchProofViewV1 | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!enabled) return;
    setBusy(true);
    setError(null);
    try {
      try {
        const persisted = await api.getCurrentClientLaunchProof(clientLinkId, token);
        setView({
          ...persisted,
          proofReferenceLabel: clientSafeProofReferenceLabel({
            rawLabel: persisted.proofReferenceLabel,
            headline: persisted.headline,
          }),
        });
        return;
      } catch (persistErr) {
        const message = persistErr instanceof Error ? persistErr.message : String(persistErr);
        if (!isClientEvidenceNotReadyError(message)) {
          throw persistErr;
        }
      }

      const snapshot = await loadPortalLaunchProofApiSnapshot(api, clientLinkId, token);
      const input = buildLaunchProofInputFromPortalApis(snapshot);
      setView(
        buildClientSafeLaunchProofView({
          ...input,
          proofId: `lp_portal_${clientLinkId}`,
        }),
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      setView(null);
      if (isClientEvidenceNotReadyError(message)) {
        setError(null);
        return;
      }
      setError(toClientApiErrorMessage(message));
    } finally {
      setBusy(false);
    }
  }, [api, clientLinkId, enabled, token]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    void (async () => {
      await load();
      if (cancelled) return;
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled, load]);

  if (error) {
    return (
      <section className="value-card" aria-label="Launch proof summary">
        <p className="value-card-eyebrow">Quality evidence</p>
        <h2 className="value-card-headline">Launch proof</h2>
        <p className="warning">{error}</p>
      </section>
    );
  }

  if (!view) {
    return (
      <section className={`value-card${busy ? " loading-panel" : ""}`} aria-label="Launch proof summary">
        <p className="value-card-eyebrow">Quality evidence</p>
        <h2 className="value-card-headline">Launch proof</h2>
        <p>{busy ? "Loading launch proof…" : "Launch proof is not available yet."}</p>
      </section>
    );
  }

  const aggregateLower = view.aggregateStatusLabel.toLowerCase();
  const statusModifier = aggregateLower.includes("pass") || aggregateLower.includes("complete")
    ? "value-card-status--ready"
    : "value-card-status--pending";

  return (
    <section className="value-card" aria-label="Launch proof summary">
      <div className="proof-card-header">
        <img src={badgeUrl} alt="" className="proof-badge" width={40} height={40} aria-hidden="true" />
        <div>
          <p className="value-card-eyebrow">Quality evidence</p>
          <h2 className="value-card-headline">Launch proof</h2>
        </div>
      </div>
      <span className={`value-card-status ${statusModifier}`}>{view.aggregateStatusLabel}</span>
      <p>{view.headline}</p>
      {view.searchReadinessExcerpt ? (
        <p>
          <strong>Readiness:</strong> {view.searchReadinessExcerpt.coverageSummary}
        </p>
      ) : null}
      {view.departureKitSummary ? <p>{view.departureKitSummary}</p> : null}
      {view.phase1Sections.length > 0 ? (
        <>
          <h3>Surface checks</h3>
          <ul className="check-chip-row">
            {view.phase1Sections.slice(0, 4).map((section) => (
              <li key={section.areaLabel} className={chipClass(section.statusLabel)}>
                {section.areaLabel}: {section.statusLabel}
              </li>
            ))}
          </ul>
        </>
      ) : null}
      <details>
        <summary>Important notes and disclaimers</summary>
        <h4>What we do not promise</h4>
        <ul>
          {view.whatWeDoNotPromise.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        <h4>Launch proof notes</h4>
        <ul>
          {view.launchProofDisclaimers.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
      </details>
    </section>
  );
}
